import styled from 'styled-components';
import { unthemed, zIndex } from 'tokens';

interface BaseProps {
	isMobile?: boolean;
}

export const Base = styled.div<BaseProps>`
	width: 100%;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`;

interface PromptProps {
	isFocused: boolean;
	isDragging?: boolean;
}

export const Prompt = styled.div<PromptProps>`
	// layout
	position: relative;
	display: flex;
	flex-direction: column;
	min-height: 0;

	// style
	background-color: ${({ theme }) => theme.gray[100]};
	border-radius: 10px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	transition: all 0.2s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

	&:focus-within {
		border-color: ${({ theme }) => theme.gray[400]};
		box-shadow: 0 0 0 3px ${({ theme }) => `${theme.blue[300]}40`}, 0 2px 8px rgba(0, 0, 0, 0.06);
	}
`;

interface MultilineInputWrapperProps {
	isMobile?: boolean;
}

export const MultilineInputWrapper = styled.div<MultilineInputWrapperProps>`
	max-height: ${({ isMobile }) => (isMobile ? '150px' : '35vh')};
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: ${({ theme }) => theme.gray[400]} ${({ theme }) => theme.gray[100]};

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: ${({ theme }) => theme.gray[100]};
	}

	&::-webkit-scrollbar-thumb {
		background-color: ${({ theme }) => theme.gray[400]};
		border-radius: 3px;
	}
	border-radius: 10px;
	text-align: left;
`;

interface ActionsProps {
	isMobile?: boolean;
}

export const Actions = styled.div<ActionsProps>`
	padding: 12px 16px;
	padding-right: ${({ isMobile }) => (isMobile ? '10px' : '16px')};
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 8px;
	border-top: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const Spacer = styled.div`
	flex: 1;
`;

export const AttachmentsWrapper = styled.div`
	padding: 10px 12px 0;
`;

interface WrapperProps {
	isMobile?: boolean;
}

export const LoadingWrapper = styled.div<WrapperProps>`
	padding-top: ${({ isMobile }) => (isMobile ? '6px' : '4px')};
`;

export const FooterRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-top: 12px;
	flex-wrap: wrap;
`;

export const FooterLink = styled.button`
	background: none;
	border: none;
	color: ${({ theme }) => theme.gray[600]};
	text-decoration: underline;
	font-size: 14px;
	font-family: inherit;
	padding: 0;
	margin: 0;
	text-align: center;

	&:hover {
		color: ${({ theme }) => theme.gray[900]};
	}
`;

export const FooterSeparator = styled.span`
	color: ${({ theme }) => theme.gray[400]};
	font-size: 14px;
`;

export const DragOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: ${({ theme }) => theme.blue[300]}AA;
	border: 2px dashed ${({ theme }) => theme.blue[500]};
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	z-index: 1000;
	transition: opacity 0.2s ease-in-out;
`;

export const DragOverlayContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
`;

export const DragOverlayText = styled.div`
	color: ${({ theme }) => theme.gray[1000]};
	font-size: 14px;
	font-weight: 500;
`;

// Context toolbar (org/personal selector integrated into input header)
export const ContextToolbar = styled.div`
	padding: 10px 12px;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 10px 10px 0 0;
	text-align: left;
`;

export const ContextDropdownWrapper = styled.div`
	position: relative;
	display: inline-block;
`;

export const ContextDropdownButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 12px;
	background: transparent;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 6px;
	color: ${({ theme }) => theme.gray[1000]};
	font-size: 14px;
	font-family: inherit;
	transition: all 0.2s ease;

	&:hover {
		background: ${({ theme }) => theme.gray[100]};
		border-color: ${({ theme }) => theme.gray[400]};
	}
`;

export const OrgIcon = styled.div`
	width: 16px;
	height: 16px;
	background: ${({ theme }) => theme.blue[500]};
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 10px;
	font-weight: 600;
	color: ${unthemed.gray[0]};
	flex-shrink: 0;
`;

export const PersonalIcon = styled.div`
	width: 16px;
	height: 16px;
	background: ${({ theme }) => theme.purple[500]};
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`;

export const DropdownArrow = styled.span`
	font-size: 10px;
	color: ${({ theme }) => theme.gray[600]};
	margin-left: 4px;
`;

export const ContextMenu = styled.div<{ $isOpen: boolean }>`
	position: absolute;
	top: 100%;
	left: 0;
	margin-top: 4px;
	background: ${({ theme }) => theme.gray[100]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 6px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
	min-width: 200px;
	padding: 4px;
	z-index: ${zIndex.island};
	opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
	pointer-events: ${({ $isOpen }) => ($isOpen ? 'all' : 'none')};
	transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-4px)')};
	transition: opacity 0.15s ease, transform 0.15s ease;
`;

export const ContextMenuItem = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 10px;
	width: 100%;
	background: transparent;
	border: none;
	border-radius: 4px;
	color: ${({ theme }) => theme.gray[1000]};
	font-size: 14px;
	font-family: inherit;
	transition: background 0.15s ease;

	&:hover {
		background: ${({ theme }) => theme.gray[200]};
	}
`;
